import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import {
  BanIcon,
  BookOpenIcon,
  BotIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleSlashIcon,
  ShieldQuestionIcon,
  SparklesIcon,
} from "lucide-react";
import {
  Trace,
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceIcon,
  TraceLine,
  TraceTitle,
} from "@/components/ui/trace";
import { MarkdownMessage } from "./MarkdownMessage";
import type { Block, QuestionBlock, ReasoningBlock, SubagentBlock, ToolBlock } from "./projection";
import { AmyQuestionnaire } from "./AmyQuestionnaire";
import { isApproval } from "./asking";
import { AnsweredQuestionLine, QuestionCard } from "./QuestionCard";
import { subagentLabel, toolTitle, type ToolPhase } from "./toolLabels";
import { isEmptyValue } from "./format";
import { ValueBlock } from "./values";

function StatusIcon({ phase, fallback }: { phase: ToolPhase; fallback: React.ReactNode }) {
  switch (phase) {
    case "running":
      return <Spinner />;
    case "done":
      return fallback;
    case "error":
      return <CircleAlertIcon />;
    case "denied":
      return <BanIcon />;
    case "stopped":
      return <CircleSlashIcon />;
    case "approval":
      return <ShieldQuestionIcon />;
  }
}

/**
 * Where a step stands. A call still marked running when the thread is no
 * longer live never finished (refresh, crash) — unless it is parked on an
 * approval, which is a legitimate wait.
 */
function toolPhase(tool: ToolBlock, live: boolean): ToolPhase {
  if (tool.status !== "running") return tool.status;
  if (tool.awaitingApproval) return "approval";
  return live ? "running" : "stopped";
}

export function ToolStep({ tool, live }: { tool: ToolBlock; live: boolean }) {
  const phase = toolPhase(tool, live);
  const title = toolTitle(tool.name, phase, tool.skill);
  const icon = <StatusIcon phase={phase} fallback={tool.skill ? <BookOpenIcon /> : <CircleCheckIcon />} />;
  const status = phase === "running" ? "running" : phase === "error" ? "error" : undefined;
  const hasInput = !tool.skill && !isEmptyValue(tool.input);
  const hasOutput = phase === "done" && !tool.skill && !isEmptyValue(tool.output);
  const hasError = phase === "error" && !!tool.error;

  const line = (
    <TraceLine status={status}>
      <TraceIcon>{icon}</TraceIcon>
      <TraceTitle>{title}</TraceTitle>
    </TraceLine>
  );
  if (phase === "running" || (!hasInput && !hasOutput && !hasError)) return line;
  return (
    <TraceCollapsible defaultOpen={phase === "error"}>
      <TraceCollapsibleTrigger>{line}</TraceCollapsibleTrigger>
      <TraceCollapsibleContent className="flex flex-col gap-2">
        {hasInput && <ValueBlock label="Input" value={tool.input} />}
        {hasOutput && <ValueBlock label="Output" value={tool.output} />}
        {hasError && <ValueBlock label="Error" value={tool.error} error />}
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}

export function ReasoningStep({ block }: { block: ReasoningBlock }) {
  // Open while thinking, closed once done — unless the user toggled it.
  const [open, setOpen] = useState<boolean>();
  return (
    <TraceCollapsible open={open ?? block.streaming} onOpenChange={setOpen}>
      <TraceCollapsibleTrigger>
        <TraceLine status={block.streaming ? "running" : undefined}>
          <TraceIcon>{block.streaming ? <Spinner /> : <SparklesIcon />}</TraceIcon>
          <TraceTitle>{block.streaming ? "Thinking" : "Thought"}</TraceTitle>
        </TraceLine>
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent className="whitespace-pre-wrap">{block.text}</TraceCollapsibleContent>
    </TraceCollapsible>
  );
}

const isPendingQuestion = (b: Block): b is QuestionBlock =>
  b.kind === "question" && (b.status === "open" || b.status === "parked");

/**
 * A delegation to one of Amy's subagents (AI Elements Subagent): one line for
 * the run, opening onto what the subagent did — its questions and tool calls
 * — and its report. Questions it is still waiting on render as cards under
 * the line so they are never hidden in a collapsed trace.
 */
export function SubagentStep({ block, live }: { block: SubagentBlock; live: boolean }) {
  const label = subagentLabel(block.name);
  const pending = block.children.filter(isPendingQuestion);
  const history = block.children.filter((b) => !isPendingQuestion(b));
  const waiting = block.status === "running" && pending.length > 0;
  const phase: ToolPhase | "waiting" =
    block.status !== "running" ? block.status : waiting ? "waiting" : live ? "running" : "stopped";
  const title =
    phase === "running"
      ? `${label} is working`
      : phase === "waiting"
        ? `${label} needs your input`
        : phase === "done"
          ? `${label} finished`
          : phase === "error"
            ? `${label} ran into a problem`
            : phase === "denied"
              ? `${label} was declined`
              : `${label} stopped`;
  const running = phase === "running" || phase === "waiting";
  const [open, setOpen] = useState<boolean>();
  const hasBody = !!block.task || history.length > 0 || !!block.output || !!block.error;

  return (
    <>
      <TraceCollapsible open={open ?? running} onOpenChange={setOpen}>
        <TraceCollapsibleTrigger disabled={!hasBody}>
          <TraceLine status={phase === "running" ? "running" : phase === "error" ? "error" : undefined}>
            <TraceIcon>
              {phase === "running" ? (
                <Spinner />
              ) : phase === "waiting" ? (
                <ShieldQuestionIcon />
              ) : phase === "done" ? (
                <BotIcon />
              ) : (
                <StatusIcon phase={phase} fallback={<BotIcon />} />
              )}
            </TraceIcon>
            <TraceTitle>{title}</TraceTitle>
          </TraceLine>
        </TraceCollapsibleTrigger>
        {hasBody && (
          <TraceCollapsibleContent className="flex flex-col gap-3">
            {block.task && (
              <p className="line-clamp-3 text-xs italic" title={block.task}>
                {block.task}
              </p>
            )}
            {history.length > 0 && (
              <Trace>
                {history.map((child) =>
                  child.kind === "tool" ? (
                    <ToolStep key={child.id} tool={child} live={live} />
                  ) : child.kind === "question" ? (
                    <AnsweredQuestionLine key={child.id} question={child} />
                  ) : null
                )}
              </Trace>
            )}
            {block.output && (
              <div className="text-foreground">
                <MarkdownMessage content={block.output} />
              </div>
            )}
            {block.error && !block.output && <p className="text-destructive">{block.error}</p>}
          </TraceCollapsibleContent>
        )}
      </TraceCollapsible>
      {/* Its open questions as one questionnaire; approvals keep their card. */}
      {pending.some((q) => !isApproval(q)) && (
        <AmyQuestionnaire questions={pending.filter((q) => !isApproval(q))} />
      )}
      {pending.filter(isApproval).map((q) => (
        <QuestionCard key={q.id} question={q} />
      ))}
    </>
  );
}
